import React, { useState, useEffect } from 'react';
import { 
  FiPrinter, FiDownload, FiSearch, FiFilter, FiCalendar, 
  FiEye, FiX, FiSave, FiUser, FiMapPin, FiPhone, FiDollarSign,
  FiBriefcase, FiClock, FiMap, FiTrash2
} from 'react-icons/fi';
import Swal from 'sweetalert2';

const API_URL = 'https://app.thundermaxhome.com/api.php';

export default function AllJobsPage() {
  const [jobs, setJobs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  
  // States สำหรับ Filter
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  // States สำหรับ Modal ดูรายละเอียด/แก้ไข
  const [showModal, setShowModal] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [editForm, setEditForm] = useState({
    id: '',
    customerName: '',
    phone: '',
    address: '',
    job_type: 'aircon',
    problem: '',
    price: 0,
    status: 'pending_approval',
    appointmentDate: '',
    appointmentTime: '',
    mapLink: '',
    techName: '',
    techPhone: ''
  });

  useEffect(() => {
    fetchJobs();
  }, []);

  const fetchJobs = () => {
    setIsLoading(true);
    fetch(`${API_URL}?action=get_all_jobs`)
      .then(res => res.json())
      .then(data => { 
        setJobs(data.data || []); 
        setIsLoading(false); 
      })
      .catch(err => { 
        console.error(err); 
        setIsLoading(false); 
      });
  };

  // 🧮 ระบบกรองข้อมูล (Filter Logic)
  const filteredJobs = jobs.filter(job => {
    const jobDate = new Date(job.createdAt);
    const isAfterStart = startDate ? jobDate >= new Date(startDate) : true;
    const isBeforeEnd = endDate ? jobDate <= new Date(endDate + 'T23:59:59') : true;
    const isStatusMatch = statusFilter === 'all' ? true : job.status === statusFilter;
    
    const safeCustomerName = job.customerName || '';
    const safePhone = job.phone || '';
    const isSearchMatch = safeCustomerName.toLowerCase().includes(searchTerm.toLowerCase()) || safePhone.includes(searchTerm);
    
    return isAfterStart && isBeforeEnd && isStatusMatch && isSearchMatch;
  });

  // 📝 แปลงสถานะเป็นภาษาไทย
  const getStatusLabel = (status) => {
    switch(status) {
      case 'pending_approval': return 'รอแอดมินยืนยัน';
      case 'pending': return 'รอเดินทาง';
      case 'traveling': return 'กำลังเดินทาง';
      case 'in_progress': return 'กำลังซ่อม';
      case 'completed': return 'เสร็จสิ้น';
      case 'cancelled': return 'ยกเลิก';
      default: return status;
    }
  };

  // 🎨 จัดหมวดหมู่สีเฉพาะสำหรับประเภทงาน (Job Type Badges)
  const getJobTypeBadge = (type) => {
    const types = {
      'aircon': { label: '❄️ แอร์บ้าน', style: 'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-900/30 dark:text-blue-400 dark:border-blue-800/50' },
      'electric': { label: '⚡ ไฟฟ้าระบบ', style: 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/30 dark:text-amber-400 dark:border-amber-800/50' },
      'contractor': { label: '🏗️ งานรับเหมา', style: 'bg-purple-50 text-purple-700 border-purple-200 dark:bg-purple-900/30 dark:text-purple-400 dark:border-purple-800/50' },
      'extension': { label: '🔨 งานต่อเติม', style: 'bg-fuchsia-50 text-fuchsia-700 border-fuchsia-200 dark:bg-fuchsia-900/30 dark:text-fuchsia-400 dark:border-fuchsia-800/50' },
      'solar': { label: '☀️ โซล่าเซลล์', style: 'bg-yellow-50 text-yellow-750 border-yellow-250 dark:bg-yellow-900/30 dark:text-yellow-400 dark:border-yellow-800/50' },
      'door': { label: '🚪 ประตูออโต้', style: 'bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-900/30 dark:text-rose-400 dark:border-rose-800/50' },
      'evertor': { label: '🛗 ระบบลิฟท์', style: 'bg-teal-50 text-teal-700 border-teal-200 dark:bg-teal-900/30 dark:text-teal-400 dark:border-teal-800/50' },
      'camera': { label: '📹 กล้องวงจร', style: 'bg-pink-50 text-pink-700 border-pink-200 dark:bg-pink-900/30 dark:text-pink-400 dark:border-pink-800/50' },
      'rework': { label: '🔧 งานแก้', style: 'bg-red-50 text-red-700 border-red-200 dark:bg-red-900/30 dark:text-red-400 dark:border-red-800/50' }
    };
    
    const t = types[type] || { label: '💼 ทั่วไป', style: 'bg-gray-50 text-gray-600 border-gray-200 dark:bg-gray-800 dark:text-gray-400 dark:border-gray-700' };
    
    return (
      <span className={`px-2 py-0.5 rounded text-[10px] font-bold border inline-flex items-center gap-1 ${t.style}`}>
        {t.label}
      </span>
    );
  };

  const getJobTypeLabelOnly = (type) => {
    const labels = {
      'aircon': 'แอร์บ้าน',
      'electric': 'ไฟฟ้าระบบ',
      'contractor': 'งานรับเหมา',
      'extension': 'งานต่อเติม',
      'solar': 'โซล่าเซลล์',
      'door': 'ประตูออโต้',
      'evertor': 'ระบบลิฟท์',
      'camera': 'กล้องวงจร',
      'rework': 'งานแก้'
    };
    return labels[type] || 'ทั่วไป';
  };

  // 📥 ฟังก์ชัน Export CSV
  const exportToCSV = () => {
    const headers = ['วันที่แจ้งงาน', 'ชื่อลูกค้า', 'เบอร์โทร', 'ประเภทงาน', 'อาการเสีย', 'ราคาสุทธิ', 'สถานะ'];
    const csvRows = filteredJobs.map(job => [
      job.createdAt, job.customerName || '-', job.phone || '-', getJobTypeLabelOnly(job.job_type),
      `"${job.problem?.replace(/"/g, '""') || ''}"`,
      job.price || '0', getStatusLabel(job.status)
    ].join(','));
    
    const csvContent = "\uFEFF" + [headers.join(','), ...csvRows].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `รายงานงานซ่อม_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  const handlePrint = () => {
    window.print();
  };

  // 🔍 เปิดดูรายละเอียด & โหลดข้อมูลใส่ Form
  const handleOpenDetails = (job) => {
    setSelectedJob(job);
    setEditForm({
      id: job.id || '',
      customerName: job.customerName || '',
      phone: job.phone || '',
      address: job.address || '',
      job_type: job.job_type || 'aircon',
      problem: job.problem || '',
      price: job.price || 0,
      status: job.status || 'pending_approval',
      appointmentDate: job.appointmentDate || '',
      appointmentTime: job.appointmentTime || '',
      mapLink: job.mapLink || '',
      techName: job.techName || '',
      techPhone: job.techPhone || ''
    });
    setShowModal(true);
  };

  // ✍️ ส่งบันทึกอัปเดตงานหลัก
  const handleEditSubmit = async (e) => {
    e.preventDefault();
    Swal.fire({ title: 'กำลังบันทึกข้อมูล...', allowOutsideClick: false, didOpen: () => Swal.showLoading() });

    try {
      const res = await fetch(`${API_URL}?action=update_job`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(editForm)
      });
      const data = await res.json();

      if (data.success) {
        Swal.fire({ icon: 'success', title: 'อัปเดตข้อมูลงานสำเร็จ!', showConfirmButton: false, timer: 1500 });
        setShowModal(false);
        fetchJobs(); // โหลดใหม่
      } else {
        throw new Error(data.message);
      }
    } catch (err) {
      Swal.fire('ล้มเหลว', err.message || 'เกิดข้อผิดพลาดในการเชื่อมต่อ', 'error');
    }
  };

  // 🗑️ ฟังก์ชันลบงาน
  const handleDeleteJob = async (jobId) => {
    const result = await Swal.fire({
      title: 'ยืนยันการลบงาน?',
      text: 'คุณต้องการลบข้อมูลคิวงานนี้ใช่หรือไม่? ข้อมูลจะไม่สามารถกู้คืนได้',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'ใช่, ลบเลย!',
      cancelButtonText: 'ยกเลิก'
    });

    if (result.isConfirmed) {
      Swal.fire({ title: 'กำลังลบข้อมูล...', allowOutsideClick: false, didOpen: () => Swal.showLoading() });
      try {
        const res = await fetch(`${API_URL}?action=delete_job`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: jobId })
        });
        const data = await res.json();
        
        if (data.success) {
          Swal.fire('ลบสำเร็จ!', 'ข้อมูลงานถูกลบออกจากระบบแล้ว', 'success');
          setShowModal(false);
          fetchJobs();
        } else {
          throw new Error(data.message || 'ไม่สามารถลบงานได้');
        }
      } catch (err) {
        Swal.fire('ล้มเหลว', err.message || 'เกิดข้อผิดพลาดในการเชื่อมต่อ', 'error');
      }
    }
  };

  return (
    <div className="space-y-6 pb-10 print:m-0 print:p-0 print:bg-white animate-fade-in text-left font-sans text-gray-800 dark:text-gray-100">
      
      {/* ส่วนหัว */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 print:hidden">
        <div>
          <h1 className="text-xl font-black text-gray-850 dark:text-white">📑 ข้อมูลงานและคิวทั้งหมด (Job Center)</h1>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            ดูประวัติงาน ค้นหารายการช่างล้างแอร์/ไฟฟ้า แก้ไขข้อมูลราคาและที่อยู่ลูกค้า รวมถึงออกรายงานประจำสัปดาห์
          </p>
        </div>
        <div className="flex gap-2 w-full md:w-auto">
          <button onClick={exportToCSV} className="flex-1 md:flex-none bg-green-600 hover:bg-green-500 text-white px-4 py-2.5 rounded-xl text-xs font-bold flex justify-center items-center gap-2 shadow-sm transition">
            <FiDownload /> Export Excel
          </button>
          <button onClick={handlePrint} className="flex-1 md:flex-none bg-indigo-600 hover:bg-indigo-500 text-white px-4 py-2.5 rounded-xl text-xs font-bold flex justify-center items-center gap-2 shadow-sm transition">
            <FiPrinter /> พิมพ์สรุป A4
          </button>
        </div>
      </div>

      {/* 🔍 Filter Area */}
      <div className="bg-white dark:bg-[#131825] p-4 rounded-2xl border border-gray-200 dark:border-[#1e2330] shadow-sm flex flex-col lg:flex-row gap-4 print:hidden transition-colors">
        <div className="relative flex-1">
          <FiSearch className="absolute left-3.5 top-3 text-gray-400" />
          <input 
            type="text" 
            placeholder="ค้นหาชื่อลูกค้า, เบอร์โทร..." 
            value={searchTerm} 
            onChange={(e) => setSearchTerm(e.target.value)} 
            className="w-full pl-10 pr-4 py-2 bg-gray-50 dark:bg-gray-800/60 border border-gray-250 dark:border-gray-800 rounded-xl text-xs dark:text-white outline-none focus:ring-1 focus:ring-indigo-500 transition-all" 
          />
        </div>
        <div className="flex items-center gap-2 flex-1">
          <FiCalendar className="text-gray-450 shrink-0" />
          <input 
            type="date" 
            value={startDate} 
            onChange={e => setStartDate(e.target.value)} 
            className="w-full p-2 bg-gray-50 dark:bg-gray-800/60 border border-gray-250 dark:border-gray-800 rounded-xl text-xs dark:text-white outline-none font-mono" 
            title="ตั้งแต่" 
          />
          <span className="text-gray-400 font-bold">-</span>
          <input 
            type="date" 
            value={endDate} 
            onChange={e => setEndDate(e.target.value)} 
            className="w-full p-2 bg-gray-50 dark:bg-gray-800/60 border border-gray-250 dark:border-gray-800 rounded-xl text-xs dark:text-white outline-none font-mono" 
            title="ถึง" 
          />
        </div>
        <div className="relative flex-1">
          <FiFilter className="absolute left-3.5 top-3.5 text-gray-400" />
          <select 
            value={statusFilter} 
            onChange={e => setStatusFilter(e.target.value)} 
            className="w-full pl-10 pr-4 py-2 bg-gray-50 dark:bg-gray-800/60 border border-gray-250 dark:border-gray-800 rounded-xl text-xs dark:text-white outline-none focus:ring-1 focus:ring-indigo-500 appearance-none font-bold text-gray-700 dark:text-gray-300 transition-colors"
          >
            <option value="all">ดูทุกสถานะคิวงาน</option>
            <option value="pending_approval">รอแอดมินยืนยัน</option>
            <option value="pending">รอเดินทาง (จ่ายงานแล้ว)</option>
            <option value="traveling">กำลังเดินทาง</option>
            <option value="in_progress">กำลังซ่อม/ให้บริการ</option>
            <option value="completed">✅ เสร็จสิ้นงานแล้ว</option>
            <option value="cancelled">❌ ยกเลิกงาน</option>
          </select>
        </div>
      </div>

      {/* 📄 Table View */}
      <div className="bg-white dark:bg-[#131825] rounded-2xl border border-gray-200 dark:border-[#1e2330] shadow-sm overflow-hidden transition-colors print:shadow-none print:border-none">
        
        {/* หัวกระดาษสำหรับโหมดพิมพ์ */}
        <div className="hidden print:block mb-4 text-center">
          <h2 className="text-xl font-black text-black">รายงานสรุปข้อมูลงานซ่อม - THUNDER MAXHOME</h2>
          <p className="text-xs text-gray-650">แสดงผลรวมทั้งหมด {filteredJobs.length} รายการ</p>
        </div>

        <div className="p-4 bg-gray-50 dark:bg-[#1b2132]/30 border-b border-gray-150 dark:border-[#1e2330] flex justify-between items-center print:hidden transition-colors">
          <span className="text-xs font-bold text-gray-600 dark:text-gray-300">
            พบข้อมูลงาน: <span className="text-indigo-600 dark:text-indigo-400 font-extrabold">{filteredJobs.length}</span> รายการ
          </span>
          <span className="text-xs font-bold text-green-600 dark:text-green-400">
            รวมราคาทั้งสิ้น: <span className="font-extrabold text-sm">฿{filteredJobs.reduce((sum, j) => sum + (Number(j.price)||0), 0).toLocaleString()}</span>
          </span>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs text-gray-700 dark:text-gray-300 print:text-black">
            <thead className="text-[11px] uppercase bg-gray-50 dark:bg-[#1b2132]/40 text-gray-500 dark:text-gray-400 border-b border-gray-150 dark:border-[#1e2330] print:bg-white print:border-b-2 print:border-black transition-colors">
              <tr>
                <th className="px-4 py-4 font-bold">วันที่แจ้งคิว</th>
                <th className="px-4 py-4 font-bold">ข้อมูลลูกค้า</th>
                <th className="px-4 py-4 font-bold">ประเภทงาน</th>
                <th className="px-4 py-4 font-bold">อาการเสียหาย/สาเหตุ</th>
                <th className="px-4 py-4 font-bold text-right">ราคา</th>
                <th className="px-4 py-4 font-bold text-center">สถานะ</th>
                <th className="px-4 py-4 font-bold text-center print:hidden">การจัดการ</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-[#1e2330] print:divide-gray-300 transition-colors">
              {isLoading ? (
                <tr><td colSpan="7" className="text-center py-10 font-bold text-indigo-500">กำลังดึงข้อมูล...</td></tr>
              ) : filteredJobs.length > 0 ? filteredJobs.map((job) => (
                <tr key={job.id} className="hover:bg-gray-50/50 dark:hover:bg-[#1b2132]/10 transition-colors">
                  <td className="px-4 py-4 whitespace-nowrap font-mono text-[10px] text-gray-500 dark:text-gray-400 print:text-black">
                    {job.createdAt ? job.createdAt.split(' ')[0] : '-'}
                  </td>
                  <td className="px-4 py-4">
                    <div className="font-bold text-gray-900 dark:text-white print:text-black">{job.customerName || 'ไม่ระบุชื่อ'}</div>
                    <div className="text-[10px] font-bold text-indigo-500 dark:text-indigo-400 print:text-gray-600">{job.phone || 'ไม่มีเบอร์'}</div>
                  </td>
                  <td className="px-4 py-4">
                    {getJobTypeBadge(job.job_type)}
                  </td>
                  <td className="px-4 py-4 max-w-[180px] truncate text-gray-600 dark:text-gray-400 print:text-black" title={job.problem}>
                    {job.problem || '-'}
                  </td>
                  <td className="px-4 py-4 text-right font-black text-green-600 dark:text-green-400 print:text-black">
                    ฿{job.price ? Number(job.price).toLocaleString() : '0'}
                  </td>
                  <td className="px-4 py-4 text-center">
                    <span className={`px-2.5 py-0.5 rounded-full text-[9px] font-bold border print:border-gray-500 print:bg-transparent print:text-black
                      ${job.status === 'completed' ? 'bg-green-50 text-green-700 border-green-200 dark:bg-green-950/20 dark:text-green-400 dark:border-green-800/30' : 
                        job.status === 'cancelled' ? 'bg-red-50 text-red-750 border-red-200 dark:bg-red-950/20 dark:text-red-400 dark:border-red-800/30' : 
                        'bg-indigo-50 text-indigo-700 border-indigo-200 dark:bg-indigo-950/20 dark:text-indigo-400 dark:border-indigo-800/30'}`}>
                      {getStatusLabel(job.status)}
                    </span>
                  </td>
                  <td className="px-4 py-4 text-center print:hidden">
                    <button 
                      onClick={() => handleOpenDetails(job)}
                      className="px-3 py-1 bg-indigo-600 hover:bg-indigo-500 text-white rounded-lg transition font-bold text-[10px] flex items-center gap-1 mx-auto shadow-sm"
                    >
                      <FiEye size={11} /> ดูข้อมูล / ปรับแก้ยอด
                    </button>
                  </td>
                </tr>
              )) : (
                <tr><td colSpan="7" className="text-center py-10 text-gray-500 font-bold">ไม่พบประวัติคิวงานในช่วงเวลานี้</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* 📄 Modal ดูรายละเอียดและแก้ไขข้อมูลงาน */}
      {showModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-[#131825] w-full max-w-2xl rounded-2xl shadow-xl border border-gray-200 dark:border-slate-800 overflow-hidden animate-zoom-in">
            {/* Modal Header */}
            <div className="p-5 border-b border-gray-100 dark:border-slate-800 flex justify-between items-center bg-gray-50 dark:bg-[#1b2132]/30">
              <div>
                <h3 className="font-black text-xs text-gray-850 dark:text-white flex items-center gap-1.5">
                  📄 อัปเดตและแก้ไขรายละเอียดคิวงาน #${editForm.id}
                </h3>
                <p className="text-[10px] text-gray-400 dark:text-gray-500 mt-0.5 font-mono">ลงทะเบียนระบบเมื่อ: {selectedJob?.createdAt}</p>
              </div>
              <button 
                onClick={() => setShowModal(false)} 
                className="text-gray-400 hover:text-gray-600 dark:hover:text-white transition"
              >
                <FiX size={20} />
              </button>
            </div>

            {/* Modal Form */}
            <form onSubmit={handleEditSubmit} className="p-6 space-y-4 max-h-[80vh] overflow-y-auto text-xs text-left">
              
              {/* Section 1: ข้อมูลลูกค้า */}
              <div className="space-y-3">
                <h4 className="font-extrabold text-[10px] text-indigo-500 uppercase tracking-wider flex items-center gap-1.5 pb-1 border-b border-gray-100 dark:border-slate-800">
                  <FiUser /> ข้อมูลส่วนตัวลูกค้า
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="font-bold text-gray-700 dark:text-gray-300">ชื่อ-นามสกุลลูกค้า</label>
                    <input 
                      type="text" 
                      required
                      value={editForm.customerName}
                      onChange={e => setEditForm({...editForm, customerName: e.target.value})}
                      className="w-full bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-xl p-2.5 text-sm text-gray-900 dark:text-white mt-1 outline-none focus:ring-2 focus:ring-indigo-500" 
                    />
                  </div>
                  <div>
                    <label className="font-bold text-gray-700 dark:text-gray-300">เบอร์โทรศัพท์ติดต่อ</label>
                    <input 
                      type="text" 
                      required
                      value={editForm.phone}
                      onChange={e => setEditForm({...editForm, phone: e.target.value})}
                      className="w-full bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-xl p-2.5 text-sm text-gray-900 dark:text-white mt-1 outline-none focus:ring-2 focus:ring-indigo-500 font-mono" 
                    />
                  </div>
                </div>
                <div>
                  <label className="font-bold text-gray-700 dark:text-gray-300 flex items-center gap-1"><FiMapPin /> ที่อยู่ติดตั้ง/สถานที่บริการ</label>
                  <textarea 
                    rows="2"
                    required
                    value={editForm.address}
                    onChange={e => setEditForm({...editForm, address: e.target.value})}
                    className="w-full bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-xl p-2.5 text-sm text-gray-900 dark:text-white mt-1 outline-none focus:ring-2 focus:ring-indigo-500" 
                    placeholder="ระบุที่อยู่หน้างานจริงของลูกค้า"
                  />
                </div>
              </div>

              {/* Section 2: รายละเอียดราคาและสถานะงาน */}
              <div className="space-y-3 pt-2">
                <h4 className="font-extrabold text-[10px] text-indigo-500 uppercase tracking-wider flex items-center gap-1.5 pb-1 border-b border-gray-100 dark:border-slate-800">
                  <FiBriefcase /> รายละเอียดหน้างานและค่าบริการ
                </h4>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="font-bold text-gray-700 dark:text-gray-300">ประเภทบริการ</label>
                    <select 
                      value={editForm.job_type} 
                      onChange={e => setEditForm({...editForm, job_type: e.target.value})}
                      className="w-full bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-xl p-2.5 text-sm text-gray-900 dark:text-white mt-1 outline-none focus:ring-2 focus:ring-indigo-500"
                    >
                      <option value="aircon">❄️ แอร์บ้าน</option>
                      <option value="electric">⚡ ไฟฟ้าระบบ</option>
                      <option value="contractor">🏗️ งานรับเหมา</option>
                      <option value="extension">🔨 งานต่อเติม</option>
                      <option value="solar">☀️ โซล่าเซลล์</option>
                      <option value="door">🚪 ประตูออโต้</option>
                      <option value="evertor">🛗 ระบบลิฟท์</option>
                      <option value="camera">📹 กล้องวงจร</option>
                      <option value="rework">🔧 งานแก้ (รับประกัน)</option>
                      <option value="general">💼 ทั่วไป</option>
                    </select>
                  </div>
                  <div>
                    <label className="font-bold text-gray-700 dark:text-gray-300">ราคาสุทธิ (บาท)</label>
                    <input 
                      type="number" 
                      required
                      value={editForm.price}
                      onChange={e => setEditForm({...editForm, price: Number(e.target.value)})}
                      className="w-full bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-xl p-2.5 text-sm text-gray-900 dark:text-white mt-1 outline-none focus:ring-2 focus:ring-indigo-500 font-bold text-green-600 dark:text-green-400 font-mono" 
                    />
                  </div>
                  <div>
                    <label className="font-bold text-gray-700 dark:text-gray-300">สถานะงานปัจจุบัน</label>
                    <select 
                      value={editForm.status} 
                      onChange={e => setEditForm({...editForm, status: e.target.value})}
                      className="w-full bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-xl p-2.5 text-sm text-gray-900 dark:text-white mt-1 outline-none focus:ring-2 focus:ring-indigo-500 font-bold"
                    >
                      <option value="pending_approval">🕒 รอแอดมินยืนยัน</option>
                      <option value="pending">🚗 รอเดินทาง</option>
                      <option value="traveling">🚀 กำลังเดินทาง</option>
                      <option value="in_progress">🔧 กำลังซ่อม</option>
                      <option value="completed">✅ เสร็จสิ้น</option>
                      <option value="cancelled">❌ ยกเลิก</option>
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="font-bold text-gray-700 dark:text-gray-300">วันที่นัดหมายคิว</label>
                    <input 
                      type="date" 
                      value={editForm.appointmentDate}
                      onChange={e => setEditForm({...editForm, appointmentDate: e.target.value})}
                      className="w-full bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-xl p-2.5 text-sm text-gray-900 dark:text-white mt-1 outline-none focus:ring-2 focus:ring-indigo-500 font-mono" 
                    />
                  </div>
                  <div>
                    <label className="font-bold text-gray-700 dark:text-gray-300">เวลานัดหมายคิว</label>
                    <input 
                      type="text" 
                      value={editForm.appointmentTime}
                      onChange={e => setEditForm({...editForm, appointmentTime: e.target.value})}
                      placeholder="เช่น 10:00 หรือ เช้า"
                      className="w-full bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-xl p-2.5 text-sm text-gray-900 dark:text-white mt-1 outline-none focus:ring-2 focus:ring-indigo-500" 
                    />
                  </div>
                  <div>
                    <label className="font-bold text-gray-700 dark:text-gray-300 flex items-center gap-1"><FiMap /> ลิงก์แผนที่ (Map URL)</label>
                    <input 
                      type="text" 
                      value={editForm.mapLink}
                      onChange={e => setEditForm({...editForm, mapLink: e.target.value})}
                      placeholder="https://maps.google.com/..."
                      className="w-full bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-xl p-2.5 text-sm text-gray-900 dark:text-white mt-1 outline-none focus:ring-2 focus:ring-indigo-500" 
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="font-bold text-gray-700 dark:text-gray-300">ช่างเทคนิคผู้ดูแล</label>
                    <input 
                      type="text" 
                      value={editForm.techName}
                      onChange={e => setEditForm({...editForm, techName: e.target.value})}
                      placeholder="ชื่อช่างเทคนิค"
                      className="w-full bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-xl p-2.5 text-sm text-gray-900 dark:text-white mt-1 outline-none focus:ring-2 focus:ring-indigo-500" 
                    />
                  </div>
                  <div>
                    <label className="font-bold text-gray-700 dark:text-gray-300">เบอร์โทรช่าง</label>
                    <input 
                      type="text" 
                      value={editForm.techPhone}
                      onChange={e => setEditForm({...editForm, techPhone: e.target.value})}
                      placeholder="เบอร์ติดต่อช่าง"
                      className="w-full bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-xl p-2.5 text-sm text-gray-900 dark:text-white mt-1 outline-none focus:ring-2 focus:ring-indigo-500 font-mono" 
                    />
                  </div>
                </div>

                <div>
                  <label className="font-bold text-gray-700 dark:text-gray-300">สาเหตุ / รายละเอียดอาการเสีย / หมายเหตุการปฏิบัติงาน</label>
                  <textarea 
                    rows="3"
                    value={editForm.problem}
                    onChange={e => setEditForm({...editForm, problem: e.target.value})}
                    className="w-full bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-xl p-2.5 text-sm text-gray-900 dark:text-white mt-1 outline-none focus:ring-2 focus:ring-indigo-500" 
                  />
                </div>
              </div>

              {/* Modal Actions */}
              <div className="flex justify-between items-center pt-4 border-t border-gray-100 dark:border-slate-800 mt-6">
                <div>
                  {editForm.id && (
                    <button 
                      type="button"
                      onClick={() => handleDeleteJob(editForm.id)}
                      className="px-4 py-2 bg-red-100 hover:bg-red-200 dark:bg-red-900/30 dark:hover:bg-red-900/50 text-red-600 dark:text-red-400 rounded-xl transition font-bold flex items-center gap-1.5"
                    >
                      <FiTrash2 /> ลบงานนี้
                    </button>
                  )}
                </div>
                <div className="flex gap-3">
                  <button 
                    type="button"
                    onClick={() => setShowModal(false)}
                    className="px-4 py-2 bg-gray-100 hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-xl transition font-bold"
                  >
                    ยกเลิก
                  </button>
                  <button 
                    type="submit"
                    className="px-5 py-2 bg-indigo-600 hover:bg-indigo-550 text-white rounded-xl transition font-bold flex items-center gap-1.5 shadow-sm shadow-indigo-500/10"
                  >
                    <FiSave /> บันทึกการอัปเดตงาน
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}